// ── LanguageContext (Fase 0 bilingüe ES/CA) ─────────────────────────────────
// Regla de precedencia del idioma:
//   1. Cliente LOGUEADO → manda su `idioma_preferido` de la ficha (BD).
//   2. Invitado (landing/login) → localStorage.
//   3. Nada guardado → español.
// Si un cliente logueado cambia el idioma desde el conmutador, se persiste en
// su ficha con el MISMO endpoint self-service que usa MiPerfil
// (POST /client/idioma-preferido) — una sola autopista, sin caminos paralelos.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import i18n, { IDIOMAS, IDIOMA_STORAGE_KEY } from '../i18n';
import { API } from '../api';
import { AuthContext } from './AuthContext';

const LanguageContext = createContext({ idioma: 'es', cambiarIdioma: () => {} });

export const LanguageProvider = ({ children }) => {
  const auth = useContext(AuthContext); // null si se monta fuera de AuthProvider (degrada a invitado)
  const user = auth?.user || null;
  const esCliente = !!user && user.role === 'cliente';
  const [idioma, setIdioma] = useState(i18n.language || 'es');

  const cambiarIdioma = useCallback((nuevo, { persistirPerfil = true } = {}) => {
    if (!IDIOMAS.includes(nuevo)) return;
    i18n.changeLanguage(nuevo);
    setIdioma(nuevo);
    try { localStorage.setItem(IDIOMA_STORAGE_KEY, nuevo); } catch (e) { /* sin localStorage → solo esta sesión */ }
    if (persistirPerfil && esCliente) {
      // Fire-and-forget: si falla, el idioma queda aplicado en local igualmente.
      axios.post(`${API}/client/idioma-preferido`, { idioma_preferido: nuevo }).catch(() => {});
    }
  }, [esCliente]);

  // Al entrar un cliente logueado, su preferencia de ficha MANDA sobre el localStorage.
  useEffect(() => {
    if (!esCliente) return undefined;
    let alive = true;
    axios.get(`${API}/client/mi-perfil`)
      .then(r => {
        const val = r.data?.personal?.idioma_preferido;
        if (alive && IDIOMAS.includes(val) && val !== i18n.language) {
          cambiarIdioma(val, { persistirPerfil: false }); // viene DE la ficha: no re-escribirla
        }
      })
      .catch(() => { /* sin ficha → se queda el idioma de invitado */ });
    return () => { alive = false; };
  }, [esCliente, cambiarIdioma]);

  return (
    <LanguageContext.Provider value={{ idioma, cambiarIdioma }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useIdioma = () => useContext(LanguageContext);
export default LanguageContext;
